import { useMemo } from "react";
import { buildBottleneckHints, buildLoadSnapshot } from "../i18n/deploymentBottlenecks";
import { useI18n } from "../i18n/useI18n";
import type { UiKey } from "../i18n/ui";
import type { AudienceMode } from "../lib/audienceMode";
import type { DeploymentStackConfig } from "../lib/deploymentStack";
import type { Workload } from "../lib/types";

type Props = {
  workload: Workload;
  stack: DeploymentStackConfig;
  audienceMode: AudienceMode;
  t: (key: UiKey) => string;
};

export function LoadAndBottlenecksCard({ workload, stack, audienceMode, t }: Props) {
  const { locale } = useI18n();
  const snap = useMemo(
    () => buildLoadSnapshot(locale, workload, audienceMode),
    [locale, workload, audienceMode],
  );
  const bottlenecks = useMemo(
    () => buildBottleneckHints(locale, workload, stack, audienceMode),
    [locale, workload, stack, audienceMode],
  );

  return (
    <div className="wrap">
      <div className="col">
        <h2>{t("load.title")}</h2>
        <p className="hint">
          {t(audienceMode === "simple" ? "load.hintSimple" : "load.hint")}
        </p>
        <ul className="list">
          {snap.lines.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </div>
      <div className="col">
        <h2>{t("bottleneck.title")}</h2>
        <ul className="list bottlenecks">
          {bottlenecks.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </div>
      <style>{`
        .wrap {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }
        @media (max-width: 800px) {
          .wrap {
            grid-template-columns: 1fr;
          }
        }
        h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .hint {
          margin: 0 0 0.65rem;
          font-size: 0.8rem;
          color: var(--muted);
        }
        .list {
          margin: 0;
          padding-left: 1.1rem;
          font-size: 0.84rem;
          line-height: 1.5;
          color: var(--text);
        }
        .list li {
          margin: 0.4rem 0;
        }
        .bottlenecks li {
          color: #e8c080;
        }
      `}</style>
    </div>
  );
}
